import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Paper, Stack, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import financeService from '../../../services/financeService';
import { useTimezone } from '../../../hooks/useTimezone';
import SearchableSelect, { SelectOption } from '../../common/SearchableSelect';
import Amount from '../ledger/Amount';
import { CashDifference } from './CashCountStatus';

/** Pakistani notes and coins, in rupees. */
const DENOMINATIONS = [5000, 1000, 500, 100, 75, 50, 20, 10, 5, 2, 1];

/**
 * The cashier's day close: count each note and coin in a till, see it against
 * what the books say the till held at the end of that day, and explain any
 * difference. The server posts the difference to Cash over / short — at once,
 * or after a second person approves.
 */
const CountCashDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  tills: SelectOption[];
  requiresApproval: boolean;
}> = ({ open, onClose, onSaved, tills, requiresApproval }) => {
  const theme = useTheme();
  const { getToday } = useTimezone();
  const { enqueueSnackbar } = useSnackbar();
  const [accountId, setAccountId] = useState('');
  const [day, setDay] = useState('');
  const [counts, setCounts] = useState<Record<number, string>>({});
  const [note, setNote] = useState('');
  const [expected, setExpected] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setAccountId(tills[0]?.value ?? '');
    setDay(getToday());
    setCounts({});
    setNote('');
    setError(null);
  }, [open, tills, getToday]);

  useEffect(() => {
    if (!open || !accountId || !day) return;
    let live = true;
    setExpected(null);
    financeService.getExpectedCash(accountId, day)
      .then(r => { if (live) setExpected(r.expectedPaisa); })
      .catch((e: any) => { if (live) setError(e?.message || 'Could not read what the books expect.'); });
    return () => { live = false; };
  }, [open, accountId, day]);

  const rows = DENOMINATIONS.map(value => {
    const n = Number(counts[value] || 0);
    return { value, count: Number.isInteger(n) && n >= 0 ? n : NaN };
  });
  const valid = rows.every(r => !Number.isNaN(r.count));
  const countedPaisa = rows.reduce((s, r) => s + (Number.isNaN(r.count) ? 0 : r.value * 100 * r.count), 0);
  const difference = expected == null ? null : countedPaisa - expected;
  const needsNote = difference != null && difference !== 0;
  const canSave = !busy && valid && !!accountId && !!day && expected != null && (!needsNote || !!note.trim());

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const count = await financeService.recordCashCount({
        accountId,
        day,
        denominations: rows.filter(r => r.count > 0),
        note: note.trim() || undefined,
      });
      enqueueSnackbar(
        count.status === 'AGREED' ? 'Counted — the till agrees with the books'
          : count.status === 'PENDING' ? 'Counted — the difference waits for approval'
            : 'Counted — the difference is posted to Cash over / short',
        { variant: 'success' },
      );
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Could not save the count.');
    } finally {
      setBusy(false);
    }
  };

  const figure = (label: string, value: React.ReactNode, emphasis = false) => (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</Typography>
      <Box sx={{ mt: 0.25, '& .MuiTypography-root': { fontSize: emphasis ? '1.25rem' : '1.1rem', fontWeight: 700 } }}>{value}</Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
      <DialogTitle component="div" sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={700}>Count cash</Typography>
        <Typography variant="body2" color="text.secondary">Count every note and coin in the till at the end of the day.</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <Box sx={{ flex: 1 }}><SearchableSelect label="Till" value={accountId} onChange={setAccountId} options={tills} minWidth={220} /></Box>
          <TextField
            size="small" type="date" label="Day" value={day}
            onChange={e => setDay(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: getToday() }}
            sx={{ width: { sm: 180 } }}
          />
        </Stack>

        {/* Notes and coins in two columns, so the totals below stay in view while counting. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2, rowGap: 1, mb: 2 }}>
          {rows.map(r => (
            <Stack
              key={r.value} direction="row" alignItems="center" spacing={1.5}
              sx={{ px: 1.5, py: 0.75, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: r.count > 0 ? alpha(theme.palette.primary.main, 0.04) : 'transparent' }}
            >
              <Typography variant="body2" fontWeight={700} sx={{ width: 48, fontVariantNumeric: 'tabular-nums' }}>{r.value.toLocaleString()}</Typography>
              <TextField
                size="small" type="number" placeholder="0"
                value={counts[r.value] ?? ''}
                onChange={e => setCounts(c => ({ ...c, [r.value]: e.target.value }))}
                error={Number.isNaN(r.count)}
                inputProps={{ min: 0, step: 1, 'aria-label': `Count of ${r.value}` }}
                InputProps={{ startAdornment: <InputAdornment position="start">×</InputAdornment> }}
                sx={{ width: 104, '& input': { py: 0.75, fontVariantNumeric: 'tabular-nums' } }}
              />
              <Box sx={{ flex: 1, textAlign: 'right' }}>
                <Amount paisa={Number.isNaN(r.count) ? 0 : r.value * 100 * r.count} />
              </Box>
            </Stack>
          ))}
        </Box>

        <Paper
          variant="outlined"
          sx={{
            p: 2, borderRadius: 2, mb: 2,
            bgcolor: alpha(difference != null && difference < 0 ? theme.palette.error.main : theme.palette.success.main, 0.04),
          }}
        >
          <Stack direction="row" spacing={2}>
            {figure('Books expect', expected == null ? <Typography color="text.disabled">…</Typography> : <Amount paisa={expected} blankZero={false} />)}
            {figure('Counted', <Amount paisa={countedPaisa} blankZero={false} />)}
            {figure('Difference', difference == null ? <Typography color="text.disabled">…</Typography> : <CashDifference paisa={difference} />, true)}
          </Stack>
        </Paper>

        {needsNote && (
          <>
            <TextField
              fullWidth multiline minRows={2} size="small" label="What explains the difference?" required
              value={note} onChange={e => setNote(e.target.value)}
              sx={{ mb: 1.5 }}
            />
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
              The difference posts to Cash over / short
              {requiresApproval ? ' once someone else approves it in Finance → Approvals.' : ' as soon as you save.'}
            </Alert>
          </>
        )}
        {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color="inherit" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={save} disabled={!canSave}>
          {busy ? 'Saving…' : needsNote && requiresApproval ? 'Save and send for approval' : 'Save count'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CountCashDialog;
